<template>
  <section class="content-section">
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading posts...</p>
    </div>

    <div v-else-if="posts.length > 0" class="cards-grid">
      <article v-for="post in posts" :key="post.id || post.title" class="project-card">
        <div class="card-body">
          <h2 class="card-title">{{ post.title }}</h2>
          <p class="card-date">{{ post.date }}</p>

          <div class="card-content">
            <!-- Points List Format -->
            <div v-if="post.points && post.points.length" class="points-list">
              <p v-for="(point, idx) in post.points" :key="idx" class="point-item">
                <strong class="point-label">{{ point.label }}:</strong>
                <span class="point-text"> {{ point.text }}</span>
              </p>
            </div>

            <!-- Single Summary Format -->
            <div v-else class="summary-block">
              <p class="summary-text">
                <strong v-if="post.leadLabel" class="point-label">{{ post.leadLabel }}:</strong>
                <span> {{ post.summary }}</span>
              </p>
              <p v-if="post.extraNote" class="extra-note">
                {{ post.extraNote }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="post.link" class="card-footer">
          <a
            :href="post.link"
            target="_blank"
            rel="noopener noreferrer"
            class="action-btn"
          >
            <span>{{ post.linkText || 'Check it out 👉🏼' }}</span>
          </a>
        </div>
      </article>
    </div>

    <div v-else class="empty-state">
      <p>No posts found.</p>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import contentService from '../services/contentService';

const posts = ref([]);
const isLoading = ref(true);

onMounted(async () => {
  try {
    posts.value = await contentService.getPosts();
  } catch (error) {
    console.error('Error fetching posts:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.content-section {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 60px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
}

.project-card {
  background: var(--card-bg);
  color: var(--text-dark);
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid var(--border-card);
}

.project-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

.card-body {
  flex-grow: 1;
}

.card-title {
  font-size: 1.45rem;
  font-weight: 600;
  color: var(--accent-blue);
  line-height: 1.35;
  margin-bottom: 6px;
}

.card-date {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-bottom: 20px;
}

.card-content {
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
}

.point-item {
  margin-bottom: 12px;
}

.point-item:last-child {
  margin-bottom: 0;
}

.point-label {
  color: #111827;
  font-weight: 600;
}

.summary-text {
  margin-bottom: 10px;
}

.extra-note {
  margin-top: 8px;
  color: #6b7280;
  font-style: italic;
}

.card-footer {
  margin-top: 24px;
  display: flex;
  align-items: center;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: #ebeef2;
  color: #1f2937;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  text-decoration: none;
}

.action-btn:hover {
  background-color: #dfe3e8;
  color: #111827;
  transform: translateY(-1px);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-white-dim);
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--accent-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 860px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .project-card {
    padding: 24px;
  }
  .card-title {
    font-size: 1.3rem;
  }
}
</style>